import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import useDrawStore from '@/stores/drawStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/ws';

export function useCollaboration(diagramId, user) {
  const socketRef = useRef(null);
  const {
    setConnected,
    updateCollaborators,
    applyRemoteChanges,
    updateCollaboratorCursor,
    elements,
  } = useDrawStore();

  // Connect to WebSocket
  useEffect(() => {
    if (!diagramId || !user) return;

    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', async () => {
      console.log('WebSocket connected');
      setConnected(true);

      // Join diagram session
      socket.emit('join_diagram', {
        diagram_id: diagramId,
        user_id: user.id,
        user_name: `${user.first_name} ${user.last_name}`,
      }, (response) => {
        if (response.success) {
          console.log('Joined diagram session with color:', response.color);
          updateCollaborators(response.collaborators || []);
        }
      });
    });

    socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setConnected(false);
    });

    // Handle other users joining
    socket.on('user_joined', (data) => {
      console.log('User joined:', data.name);
      useDrawStore.getState().updateCollaborators([
        ...useDrawStore.getState().collaborators,
        {
          userId: data.user_id,
          name: data.name,
          color: data.color,
          cursor: { x: 0, y: 0 },
        },
      ]);
    });

    // Handle other users leaving
    socket.on('user_left', (data) => {
      console.log('User left:', data.name);
      useDrawStore.getState().updateCollaborators(
        useDrawStore.getState().collaborators.filter(c => c.userId !== data.user_id)
      );
    });

    // Handle cursor updates from other users
    socket.on('cursor_update', (data) => {
      updateCollaboratorCursor(data.user_id, data.cursor);
    });

    // Handle element additions from other users
    socket.on('element_added', (data) => {
      applyRemoteChanges([{ type: 'add', element: data.element }]);
    });

    // Handle element updates from other users
    socket.on('element_updated', (data) => {
      applyRemoteChanges([{ 
        type: 'update', 
        elementId: data.element_id, 
        updates: data.updates 
      }]);
    });

    // Handle element deletions from other users
    socket.on('elements_deleted', (data) => {
      data.element_ids.forEach(id => {
        applyRemoteChanges([{ type: 'delete', elementId: id }]);
      });
    });

    // Handle full sync from other users
    socket.on('elements_synced', (data) => {
      useDrawStore.getState().loadDocument({
        elements: data.elements,
      });
    });

    return () => {
      if (socket) {
        socket.emit('leave_diagram', {
          diagram_id: diagramId,
          user_id: user.id,
        });
        socket.disconnect();
      }
    };
  }, [diagramId, user]);

  // Emit cursor movement (throttled)
  const emitCursorMove = useCallback((cursor) => {
    if (socketRef.current?.connected && diagramId && user) {
      socketRef.current.emit('cursor_move', {
        diagram_id: diagramId,
        user_id: user.id,
        cursor,
      });
    }
  }, [diagramId, user]);

  // Emit element added
  const emitElementAdd = useCallback((element) => {
    if (socketRef.current?.connected && diagramId) {
      socketRef.current.emit('element_add', {
        diagram_id: diagramId,
        element,
      });
    }
  }, [diagramId]);

  // Emit element updated
  const emitElementUpdate = useCallback((elementId, updates) => {
    if (socketRef.current?.connected && diagramId) {
      socketRef.current.emit('element_update', {
        diagram_id: diagramId,
        element_id: elementId,
        updates,
      });
    }
  }, [diagramId]);

  // Emit element(s) deleted
  const emitElementDelete = useCallback((elementIds) => {
    if (socketRef.current?.connected && diagramId) {
      socketRef.current.emit('element_delete', {
        diagram_id: diagramId,
        element_ids: elementIds,
      });
    }
  }, [diagramId]);

  // Emit full sync
  const emitSync = useCallback(() => {
    if (socketRef.current?.connected && diagramId) {
      socketRef.current.emit('elements_sync', {
        diagram_id: diagramId,
        elements,
      });
    }
  }, [diagramId, elements]);

  return {
    emitCursorMove,
    emitElementAdd,
    emitElementUpdate,
    emitElementDelete,
    emitSync,
  };
}
